import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FileText, Sparkles, Wand2, ChevronRight, PanelRightClose, PanelRight, Fullscreen } from 'lucide-react';
import { useQuery } from 'react-query';
import { useAskSidebar, type SidebarTab } from '../contexts/AskSidebarContext';
import { useDocumentViewer } from '../contexts/DocumentViewerContext';
import { useResizable } from '../hooks/useResizable';
import { documentApi } from '../services/api';
import { reportApi } from '../services/reportApi';
import { AssistantTab } from './sidebar/AssistantTab';
import { DocumentsTab } from './sidebar/DocumentsTab';
import { WizardTab } from './sidebar/WizardTab';

const TAB_CONFIG: Array<{
  id: SidebarTab;
  label: string;
  icon: React.FC<{ className?: string }>;
  color: string;        // inactive icon colour
  activeBg: string;     // subtle background tint when inactive
}> = [
  { id: 'documents', label: 'Medical Records', icon: FileText, color: 'text-cyan-700 dark:text-cyan-400', activeBg: 'bg-cyan-300 dark:bg-cyan-800/60' },
  { id: 'assistant', label: 'Case Assistant', icon: Sparkles, color: 'text-green-700 dark:text-green-400', activeBg: 'bg-green-300 dark:bg-green-800/60' },
  { id: 'wizard', label: 'Drafting', icon: Wand2, color: 'text-purple-700 dark:text-purple-400', activeBg: 'bg-purple-300 dark:bg-purple-800/60' },
];

/** Width of the protruding paper-organiser tabs (px) */
const TAB_STRIP_W = 34;
/** Height of each tab (px) — tall enough for icon + rotated label */
const TAB_H = 160;

/**
 * Unified three-tab sidebar with a paper-organiser metaphor.
 *
 * Three vertical tabs protrude from the left edge of the panel like physical
 * folder dividers. The active tab merges seamlessly into the panel; inactive
 * tabs sit behind it.
 */
export const UnifiedSidebar: React.FC = () => {
  const {
    isOpen, open, close, toggle, pageContext, isMaximized, toggleMaximize, exitMaximize, messages,
    activeSidebarTab, setActiveSidebarTab,
  } = useAskSidebar();
  const { activeDocument } = useDocumentViewer();

  const isVisible = pageContext.pageType !== 'other';

  // Document count for the Docs tab label (reads from react-query cache)
  const { data: caseDocuments } = useQuery(
    ['documents', pageContext.caseId],
    () => documentApi.getCaseDocuments(pageContext.caseId!),
    { enabled: !!pageContext.caseId, staleTime: 5 * 60 * 1000 },
  );
  const docCount = caseDocuments
    ? caseDocuments.filter((d: any) => d.documentType !== 'dicom_image').length
    : 0;

  // Report count for the Wizard tab label
  const { data: caseReports } = useQuery(
    ['caseReports', pageContext.caseId],
    () => reportApi.getReportsByCase(pageContext.caseId!),
    { enabled: !!pageContext.caseId, staleTime: 30_000 },
  );
  const reportCount = caseReports?.length ?? 0;

  // Track half the viewport width for max resize
  const [halfVw, setHalfVw] = useState(() => Math.floor(window.innerWidth / 2));
  useEffect(() => {
    const onResize = () => setHalfVw(Math.floor(window.innerWidth / 2));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { width: panelWidth, setWidth: setPanelWidth, handleMouseDown, minWidth: panelMinWidth, maxWidth: panelMaxWidth } = useResizable({
    defaultWidth: 615,
    minWidth: 615,
    maxWidth: halfVw,
    side: 'left',
    storageKey: 'unified-sidebar-width-v2',
    cssVar: '--sidebar-panel-width',
    cssVarInactive: '0px',
    active: isVisible && isOpen && !isMaximized,
  });

  // ── Size-preset controls (1×, 1.25×, maximise) ───────────────────
  const setDefaultSize = useCallback(() => {
    if (isMaximized) { toggleMaximize(); open(); }
    setPanelWidth(panelMinWidth);
  }, [isMaximized, toggleMaximize, open, setPanelWidth, panelMinWidth]);

  const setLargeSize = useCallback(() => {
    if (isMaximized) { toggleMaximize(); open(); }
    setPanelWidth(panelMaxWidth);
  }, [isMaximized, toggleMaximize, open, setPanelWidth, panelMaxWidth]);

  // Respond to sidebar width requests from child components (e.g. DocumentPanel size presets)
  const { pendingSidebarWidth, clearPendingSidebarWidth } = useAskSidebar();
  useEffect(() => {
    if (!pendingSidebarWidth) return;
    if (pendingSidebarWidth === 'min') setPanelWidth(panelMinWidth);
    else if (pendingSidebarWidth === 'max') setPanelWidth(panelMaxWidth);
    clearPendingSidebarWidth();
  }, [pendingSidebarWidth, clearPendingSidebarWidth, setPanelWidth, panelMinWidth, panelMaxWidth]);

  // --sidebar-width (read by Layout for marginRight) = panel + tab strip
  useLayoutEffect(() => {
    if (!isVisible || isMaximized) {
      document.documentElement.style.setProperty('--sidebar-width', '0px');
    } else if (!isOpen) {
      document.documentElement.style.setProperty('--sidebar-width', `${TAB_STRIP_W}px`);
    } else {
      document.documentElement.style.setProperty('--sidebar-width', `${panelWidth + TAB_STRIP_W}px`);
    }
    return () => {
      document.documentElement.style.setProperty('--sidebar-width', '0px');
    };
  }, [isVisible, isOpen, isMaximized, panelWidth]);

  // Auto-switch to documents tab on citation click
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isMaximizedRef = useRef(isMaximized);
  isMaximizedRef.current = isMaximized;
  useEffect(() => {
    if (activeDocument && isVisibleRef.current) {
      setActiveSidebarTab('documents');
      if (isMaximizedRef.current) exitMaximize();
      if (!isOpenRef.current) open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocument]);

  // Cmd+Shift+A keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'a') {
        e.preventDefault();
        if (pageContext.pageType !== 'other') toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle, pageContext.pageType]);

  if (!isVisible) return null;

  // ── Size-preset header (shared between normal & maximised views) ──
  // Derive from the actual panel width so the toggle reflects restored
  // localStorage state (rather than a separate `sizeMode` that always
  // started at 'default' on mount and misrepresented the real width).
  const activeSize: 'compact' | 'wide' | 'full' = isMaximized
    ? 'full'
    : panelWidth >= (panelMinWidth + panelMaxWidth) / 2
      ? 'wide'
      : 'compact';

  const sizePresets: Array<{
    id: 'compact' | 'wide' | 'full';
    label: string;
    tooltip: string;
    icon: React.FC<{ className?: string }>;
    onClick: () => void;
  }> = [
    { id: 'compact', label: 'Compact', tooltip: 'Compact sidebar — more room for your case', icon: PanelRightClose, onClick: setDefaultSize },
    { id: 'wide',    label: 'Wide',    tooltip: 'Wide sidebar — balanced view',              icon: PanelRight,      onClick: setLargeSize },
    { id: 'full',    label: 'Full',    tooltip: 'Full screen — sidebar takes the whole view', icon: Fullscreen,      onClick: toggleMaximize },
  ];

  const sizeHeader = (
    <div className="flex items-center justify-between px-2 py-1.5 border-b border-medical-100 dark:border-medical-700 flex-shrink-0">
      <div className="flex items-center rounded-lg border border-medical-200 dark:border-medical-700 overflow-hidden flex-shrink-0 bg-medical-50/50 dark:bg-medical-900/30">
        {sizePresets.map((preset, i) => {
          const Icon = preset.icon;
          const isActive = activeSize === preset.id;
          return (
            <React.Fragment key={preset.id}>
              {i > 0 && <div className="w-px h-4 bg-medical-200 dark:bg-medical-700" />}
              <button
                onClick={preset.onClick}
                className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium transition-all ${
                  isActive
                    ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white shadow-sm'
                    : 'text-medical-500 hover:bg-medical-100 hover:text-medical-700 dark:text-medical-400 dark:hover:bg-medical-700 dark:hover:text-medical-200'
                }`}
                title={preset.tooltip}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{preset.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <button
        onClick={() => { if (isMaximized) toggleMaximize(); close(); }}
        className="flex items-center gap-1 px-2 py-1 text-[11px] text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 rounded transition-colors"
        title="Close sidebar"
      >
        <span>Close</span>
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  // Maximised wizard tab → full-viewport overlay with just the wizard content
  if (isMaximized && activeSidebarTab === 'wizard') {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-medical-900 flex flex-col">
        {sizeHeader}
        <WizardTab />
      </div>
    );
  }

  // Maximised documents tab → full-viewport overlay with documents content
  if (isMaximized && activeSidebarTab === 'documents') {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-medical-900 flex flex-col">
        {sizeHeader}
        <DocumentsTab />
      </div>
    );
  }

  // Maximised assistant tab
  if (isMaximized && activeSidebarTab === 'assistant') {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-medical-900 flex flex-col">
        {sizeHeader}
        <AssistantTab />
      </div>
    );
  }

  if (isMaximized) return null;

  // ── Paper-organiser tab strip ──────────────────────────────────────
  const tabStrip = (
    <div className="flex flex-col justify-center gap-0.5 h-full" style={{ width: `${TAB_STRIP_W}px` }}>
      {TAB_CONFIG.map(tab => {
        const Icon = tab.icon;
        const isActive = activeSidebarTab === tab.id;
        const hasNotification = tab.id === 'assistant' && messages.length > 0;
        const hasDocument = tab.id === 'documents' && !!activeDocument;

        return (
          <button
            key={tab.id}
            onClick={() => {
              if (isOpen && isActive) {
                toggle();
              } else {
                setActiveSidebarTab(tab.id);
                if (!isOpen) open();
              }
            }}
            className={`
              relative flex flex-col items-center justify-center gap-2
              transition-all duration-200
              rounded-l-lg border border-medical-200 dark:border-medical-700
              ${isActive && isOpen
                ? `bg-white dark:bg-medical-800 border-r-0 ${tab.color} shadow-[-2px_0_4px_rgba(0,0,0,0.06)] z-10`
                : `${tab.activeBg} ${tab.color} border-r-0 opacity-70 hover:opacity-100`
              }
            `}
            style={{ width: isActive && isOpen ? `${TAB_STRIP_W + 1}px` : `${TAB_STRIP_W}px`, height: `${TAB_H}px` }}
            title={tab.label}
          >
            <Icon className="h-5 w-5 flex-shrink-0" />
            {/* Vertical label */}
            <span
              className="text-[9.5px] font-bold tracking-wide uppercase leading-none text-black dark:text-medical-100"
              style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}
            >
              {tab.id === 'documents' && docCount > 0
                ? `${tab.label} (${docCount})`
                : tab.id === 'wizard' && reportCount > 0
                ? `${tab.label} (${reportCount})`
                : tab.label}
            </span>
            {(hasNotification || hasDocument) && (
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
            )}
          </button>
        );
      })}
    </div>
  );

  // ── Collapsed: just the tab strip flush against right edge ─────────
  if (!isOpen) {
    return (
      <div
        className="absolute top-0 bottom-0 right-0 z-40 flex-shrink-0"
        style={{ width: `${TAB_STRIP_W}px` }}
      >
        {tabStrip}
      </div>
    );
  }

  // ── Expanded: tab strip + panel content ────────────────────────────
  return (
    <div
      data-unified-sidebar
      className="absolute top-0 bottom-0 right-0 z-40 flex"
      style={{ width: `${panelWidth + TAB_STRIP_W}px` }}
    >
      {/* Tab strip — left of panel */}
      {tabStrip}

      {/* Panel body */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-white dark:bg-medical-800 shadow-xl border-l border-medical-200 dark:border-medical-700 relative">
        {/* Resize handle — left edge of panel */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute top-0 bottom-0 left-0 w-1.5 cursor-col-resize hover:bg-primary-400/50 active:bg-primary-500/60 transition-colors z-[5]"
        />

        {sizeHeader}

        {/* Tab content — all tabs stay mounted to preserve scroll position and state */}
        <div className={`flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden ${activeSidebarTab === 'documents' ? '' : 'hidden'}`}>
          <DocumentsTab />
        </div>
        <div className={`flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden ${activeSidebarTab === 'assistant' ? '' : 'hidden'}`}>
          <AssistantTab />
        </div>
        <div className={`flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden ${activeSidebarTab === 'wizard' ? '' : 'hidden'}`}>
          <WizardTab />
        </div>
      </div>
    </div>
  );
};
